/**
 * The T box's export and print, for the native chart.
 *
 * The SSRS report's toolbar exports to Excel, PDF and images and prints. This covers the same needs
 * without a new dependency: CSV opens in Excel, the image is a PNG, and Print opens that image in a
 * window of its own, where the browser's print dialog also saves to PDF.
 *
 * Nothing in here knows what a series means. It takes what the chart has drawn and writes it out.
 */
import { ResourceSeriesVM } from '@models/backend-generated-models/models';

/**
 * One row per interval, one column per series, in the chart's own order.
 *
 * Values are written plainly - no thousands separators, full precision - so Excel reads them as
 * numbers. Dates are local, like the chart's axis, as "YYYY-MM-DD" or "YYYY-MM-DD HH:00" for hours.
 */
export function seriesToCsv(
  series: ResourceSeriesVM[],
  unit: string,
  hourly: boolean,
  averaged: boolean,
  simulated: string | null = null
): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const stamp = (d: Date) =>
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` + (hourly ? ` ${p(d.getHours())}:00` : '');

  const rows = new Map<number, { when: Date; label: string; values: (number | null)[] }>();
  series.forEach((s, col) => {
    for (const pt of s.points ?? []) {
      const d = new Date(pt.date as any);
      if (isNaN(d.getTime())) continue;
      const key = d.getTime();
      if (!rows.has(key)) rows.set(key, { when: d, label: pt.label ?? '', values: series.map(() => null) });
      rows.get(key)!.values[col] = pt.value ?? 0;
    }
  });

  const suffix = unit ? ` (${unit}${averaged ? ', average' : ''})` : (averaged ? ' (average)' : '');
  const header = ['Date', 'Period', ...series.map(s => (s.name ?? '') + suffix)];

  // A scenario's figures say so on their first line, before anything that could be mistaken for
  // readings. One cell, so it sits above the table in Excel rather than inside it.
  const lines: string[] = [];
  if (simulated) lines.push(csvCell(`Simulated figures, not metered readings: ${simulated}`));
  lines.push(header.map(csvCell).join(','));
  for (const r of Array.from(rows.values()).sort((a, b) => a.when.getTime() - b.when.getTime())) {
    lines.push([stamp(r.when), r.label, ...r.values.map(v => (v === null ? '' : String(v)))].map(csvCell).join(','));
  }

  // Excel only reads UTF-8 without mangling units like "m³" when the file starts with a BOM.
  return '\ufeff' + lines.join('\r\n') + '\r\n';
}

function csvCell(v: string): string {
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Hand a blob to the browser as a download. */
export function download(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A name that is safe on every file system. */
export function fileName(title: string, ext: string): string {
  const safe = (title || 'chart').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return `${safe}.${ext}`;
}

// ---- image ----------------------------------------------------------------------------------------

/** SVG presentation properties copied onto the clone, which leaves the page's stylesheets behind. */
const SVG_PROPS = [
  'fill', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-opacity', 'stroke-dasharray', 'opacity',
  'font-family', 'font-size', 'font-weight', 'text-anchor', 'dominant-baseline', 'visibility', 'display'
];

/**
 * Paint the chart box - the plot and everything laid over it - onto a canvas.
 *
 * Two layers, because the chart is two layers. The plot is SVG, which is cloned with its computed
 * styles written inline (an SVG drawn as an image cannot see the page's CSS) and drawn whole. The
 * numbers in the bars, the month brackets and the legend are HTML positioned over it, and are painted
 * from their own on-screen boxes: backgrounds, borders and text, in document order.
 */
export async function renderChartPng(chartBox: HTMLElement, title: string, scale = 2): Promise<Blob> {
  const box = chartBox.getBoundingClientRect();
  const titleHeight = title ? 32 : 0;

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(box.width * scale);
  canvas.height = Math.ceil((box.height + titleHeight) * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, box.width, box.height + titleHeight);

  if (title) {
    ctx.fillStyle = '#111827';
    ctx.font = '600 15px Roboto, "Helvetica Neue", Arial, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.fillText(title, 8, titleHeight / 2);
  }

  ctx.translate(0, titleHeight);

  // What sits behind the bars on screen goes behind them here too: the what-if's shaded hours. They
  // were painted last, over the bars.
  paintHtml(ctx, chartBox, box, BEHIND, true);

  // Every chart drawn in the box, not only the first: Compare's two pies are charts of their own, and
  // the picture had their titles with no pies under them.
  for (const svg of Array.from(chartBox.querySelectorAll<SVGSVGElement>('svg.ngx-charts'))) {
    const r = svg.getBoundingClientRect();
    if (!r.width || !r.height) continue;
    const img = await svgToImage(svg, r.width, r.height);
    ctx.drawImage(img, r.left - box.left, r.top - box.top, r.width, r.height);
  }

  paintHtml(ctx, chartBox, box, BEHIND, false);

  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('The chart could not be drawn.'))), 'image/png'));
}

function svgToImage(svg: SVGSVGElement, width: number, height: number): Promise<HTMLImageElement> {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  inlineStyles(svg, clone);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));

  const xml = new XMLSerializer().serializeToString(clone);
  const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('The chart could not be drawn.'));
    img.src = url;
  });
}

function inlineStyles(source: Element, target: Element): void {
  const cs = getComputedStyle(source);
  const style = SVG_PROPS.map(p => `${p}:${cs.getPropertyValue(p)}`).join(';');
  target.setAttribute('style', style);

  const s = source.children;
  const t = target.children;
  for (let i = 0; i < s.length && i < t.length; i++) inlineStyles(s[i], t[i]);
}

/** The overlays drawn behind the bars on screen. */
const BEHIND = '.rsc-guide-hours';

/**
 * Every HTML element over the plot, painted where it sits on screen. The SVG is skipped, and so is
 * anything marked data-export="skip". `behind` picks one layer: the elements matching `selector` and
 * what is inside them, or everything else.
 */
function paintHtml(ctx: CanvasRenderingContext2D, root: HTMLElement, box: DOMRect, selector: string, behind: boolean): void {
  const walk = (el: Element, inLayer: boolean) => {
    if (el instanceof SVGElement) return;
    // Screen-only chrome, such as the drill hint, is marked and left out of the picture.
    if ((el as HTMLElement).dataset?.['export'] === 'skip') return;
    const layer = inLayer || el.matches(selector);
    if (!behind && layer) return;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return;

    if (el !== root && layer === behind) {
      const r = el.getBoundingClientRect();
      const x = r.left - box.left, y = r.top - box.top;

      if (cs.backgroundColor && cs.backgroundColor !== 'rgba(0, 0, 0, 0)' && cs.backgroundColor !== 'transparent') {
        ctx.fillStyle = cs.backgroundColor;
        ctx.fillRect(x, y, r.width, r.height);
      }
      strokeBorders(ctx, cs, x, y, r.width, r.height);
    }

    for (const node of Array.from(el.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim()) {
        if (layer === behind) paintText(ctx, node as Text, cs, box);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        walk(node as Element, layer);
      }
    }
  };
  walk(root, false);
}

function strokeBorders(ctx: CanvasRenderingContext2D, cs: CSSStyleDeclaration, x: number, y: number, w: number, h: number): void {
  const side = (width: string, style: string, colour: string, draw: () => void) => {
    const px = parseFloat(width);
    if (!px || style === 'none' || style === 'hidden') return;
    ctx.strokeStyle = colour;
    ctx.lineWidth = px;
    ctx.beginPath();
    draw();
    ctx.stroke();
  };
  side(cs.borderTopWidth, cs.borderTopStyle, cs.borderTopColor, () => { ctx.moveTo(x, y); ctx.lineTo(x + w, y); });
  side(cs.borderBottomWidth, cs.borderBottomStyle, cs.borderBottomColor, () => { ctx.moveTo(x, y + h); ctx.lineTo(x + w, y + h); });
  side(cs.borderLeftWidth, cs.borderLeftStyle, cs.borderLeftColor, () => { ctx.moveTo(x, y); ctx.lineTo(x, y + h); });
  side(cs.borderRightWidth, cs.borderRightStyle, cs.borderRightColor, () => { ctx.moveTo(x + w, y); ctx.lineTo(x + w, y + h); });
}

function paintText(ctx: CanvasRenderingContext2D, node: Text, cs: CSSStyleDeclaration, box: DOMRect): void {
  const range = document.createRange();
  range.selectNodeContents(node);
  const r = range.getBoundingClientRect();
  if (!r.width || !r.height) return;

  ctx.fillStyle = cs.color;
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  ctx.textBaseline = 'middle';
  const text = (node.textContent ?? '').trim();

  // A bar's number turned to read up a narrow bar. Its on-screen box is already the turned one, so
  // the text is drawn about that box's centre, turned the same way.
  const rotate = Number(node.parentElement?.dataset['rotate'] || 0);
  if (rotate) {
    ctx.save();
    ctx.translate(r.left - box.left + r.width / 2, r.top - box.top + r.height / 2);
    ctx.rotate(rotate * Math.PI / 180);
    ctx.textAlign = 'center';
    ctx.fillText(text, 0, 0);
    ctx.restore();
    return;
  }

  ctx.fillText(text, r.left - box.left, r.top - box.top + r.height / 2);
}

/**
 * Open the image in a window of its own and print it. The browser's print dialog also offers
 * "Save as PDF". Returns false when a popup blocker stopped the window, so the caller can say so.
 *
 * The window is a page of its own, built as an HTML blob with the picture embedded, rather than an
 * empty window this one writes into. That way it does not depend on this window's object URLs
 * staying alive, and it prints itself once the picture has loaded.
 */
export async function printImage(png: Blob, title: string): Promise<boolean> {
  const w = window.open('', '_blank');
  if (!w) return false;

  const dataUrl = await blobToDataUrl(png);
  const safeTitle = escapeHtml(title || 'Chart');
  const html =
    '<!doctype html><html><head><meta charset="utf-8"><title>' + safeTitle + '</title>' +
    '<style>body{margin:0}img{width:100%;display:block}@page{size:landscape;margin:10mm}</style></head>' +
    '<body><img alt="' + safeTitle + '" src="' + dataUrl + '" onload="window.focus();window.print()"></body></html>';

  w.location.href = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  return true;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
